import React, { useState, useContext } from 'react'
import PropTypes from 'prop-types'
import { format } from 'date-fns'
import { Text, Button } from 'shared/components'
import { InWinImage } from 'routes/shared/components'
import CustomIcon from 'shared/components/customIcon'
import { ServicesContext } from 'services/context'
import { StoreContext } from 'store/context'
import { toaster } from 'utils/toast-wrapper'
import config from 'config'

import * as styles from './styles'

const MobileInwinCardComponent = ({ inwin }) => {
	const {
		triggerDTO,
		beneficiaryDTO,
		date,
		fullAmount,
		stateDescription,
		id,
	} = inwin

	const services = useContext(ServicesContext)
	const { auth } = useContext(StoreContext)
	const { memberService } = services
	const [downloadLoading, setDownloadLoading] = useState(false)
	const [emailLoading, setEmailLoading] = useState(false)

	const tournament = triggerDTO?.eventDTO?.tournamentDTO
	const eventData = triggerDTO?.eventDTO
	const { teamOne, teamTwo, sportType } = eventData || {}
	const { binaryWinner } = triggerDTO || {}

	// Check if this is a custom event (no teams)
	const isCustomEvent = !teamOne || !teamTwo || sportType === 'athletics' || sportType === 'other'

	// Format date
	const formattedDate = date ? format(new Date(date), 'MMM d, yyyy') : ''

	// Determine status and status text
	const isPlayed = stateDescription === 'WIN'
	const statusText = isPlayed ? 'Played' : 'Not Played'
	const statusColor = isPlayed ? '#6FCF97' : '#999999'

	// Check if user has complete address for receipt functionality
	const address = auth.loggedEntity?.address
	const hasAddress = Boolean(
		address?.address1 &&
		address?.city &&
		address?.zip &&
		address?.country
	)

	// Get team data - winner and loser
	const teamThatPickedForWin = binaryWinner === 'TEAM_A' ? teamOne : teamTwo
	const teamThatPickedForLose = binaryWinner === 'TEAM_A' ? teamTwo : teamOne

	// Get team logo URL
	const getTeamLogo = (team) => {
		if (team?.logo) {
			return team.logo.startsWith('http')
				? team.logo
				: `${config.assets.baseUrl}${team.logo}`
		}
		return null
	}

	// Get team abbreviations (first 3 letters of team name)
	const getTeamAbbreviation = (team) => {
		if (!team?.teamName) return '---'
		return team.teamName.substring(0, 3).toUpperCase()
	}

	const handleDownload = async () => {
		try {
			setDownloadLoading(true)
			await memberService.downloadReceipt(id)
			toaster.success('Download successful')
		} catch (error) {
			console.error('Failed to download receipt:', error)
			toaster.error('Download failed')
		} finally {
			setDownloadLoading(false)
		}
	}

	const handleEmailRequest = async () => {
		try {
			setEmailLoading(true)
			await memberService.requestReceiptByEmail(id)
			toaster.success('Email request successful')
		} catch (error) {
			console.error('Failed to request receipt by email:', error)
			toaster.error('Email request failed')
		} finally {
			setEmailLoading(false)
		}
	}

	const renderTeamSection = () => {
		if (isCustomEvent) {
			return (
				<div css={styles.customEventContainer}>
					<div css={styles.customEventIcon}>
						<CustomIcon icon="event" style={{ color: '#ffffff', fontSize: '16px' }} />
					</div>
					<div css={styles.customEventText}>
						<Text size={12} css={styles.customDescription}>
							{triggerDTO?.description || 'Custom Event'}
						</Text>
					</div>
				</div>
			)
		}

		return (
			<div css={styles.teamsContainer}>
				{/* Losing team */}
				<div css={styles.teamRow}>
					<div css={styles.teamLogoContainer}>
						{getTeamLogo(teamThatPickedForLose) ? (
							<InWinImage
								source={getTeamLogo(teamThatPickedForLose)}
								alt={teamThatPickedForLose?.teamName}
								css={styles.teamLogo}
							/>
						) : (
							<div css={[styles.teamBadge, styles.losingTeamBadge]}>
								<Text size={10} bold css={styles.teamText}>
									{getTeamAbbreviation(teamThatPickedForLose)}
								</Text>
							</div>
						)}
					</div>
					<div css={styles.teamStatusContainer}>
						<Text size={12} css={styles.teamNameText}>
							{getTeamAbbreviation(teamThatPickedForLose)}
						</Text>
						<Text size={12} css={[styles.teamStatusText, styles.losesText]}>
							Loses
						</Text>
					</div>
				</div>

				{/* Winning team */}
				<div css={styles.teamRow}>
					<div css={styles.teamLogoContainer}>
						{getTeamLogo(teamThatPickedForWin) ? (
							<InWinImage
								source={getTeamLogo(teamThatPickedForWin)}
								alt={teamThatPickedForWin?.teamName}
								css={styles.teamLogo}
							/>
						) : (
							<div css={styles.teamBadge}>
								<Text size={10} bold css={styles.teamText}>
									{getTeamAbbreviation(teamThatPickedForWin)}
								</Text>
							</div>
						)}
					</div>
					<div css={styles.teamStatusContainer}>
						<Text size={12} css={styles.teamNameText}>
							{getTeamAbbreviation(teamThatPickedForWin)}
						</Text>
						<Text size={12} css={[styles.teamStatusText, styles.winsText]}>
							Wins
						</Text>
					</div>
				</div>
			</div>
		)
	}

	const renderReceiptSection = () => {
		if (!isPlayed) {
			return null
		}

		return (
			<div css={styles.receiptSection}>
				<div css={styles.receiptHeader}>
					<CustomIcon icon="receipt" style={{ color: '#ffffff', fontSize: '16px' }} />
					<Text size={10} css={styles.receiptLabel}>
						RECEIPT
					</Text>
				</div>

				<div css={styles.receiptButtonsContainer}>
					{!hasAddress && (
						<div css={styles.addressWarning}>
							<Text size={10} css={styles.warningText}>
								Add address in your profile to download receipts
							</Text>
						</div>
					)}
					<div css={styles.receiptButtons}>
						<button
							css={[styles.receiptButton, styles.downloadButton]}
							onClick={handleDownload}
							disabled={!hasAddress || downloadLoading}
						>
							<Text size={12} bold css={styles.buttonText}>
								{downloadLoading ? 'Downloading...' : 'Download PDF'}
							</Text>
						</button>
						<button
							css={[styles.receiptButton, styles.emailButton]}
							onClick={handleEmailRequest}
							disabled={!hasAddress || emailLoading}
						>
							<Text size={12} bold css={styles.buttonText}>
								{emailLoading ? 'Requesting...' : 'Request Email'}
							</Text>
						</button>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div css={styles.cardContainer}>
			<div css={styles.cardContent}>
				{/* Top section with teams and bet info */}
				<div css={styles.topSection}>
					<div css={styles.leftSection}>
						{renderTeamSection()}
					</div>

					<div css={styles.rightSection}>
						<Text size={14} bold css={[styles.statusText, { color: statusColor }]}>
							{statusText}
						</Text>
						<Text size={16} bold css={styles.amountText}>
							${fullAmount}
						</Text>
						<Text size={12} css={styles.dateText}>
							{formattedDate}
						</Text>
					</div>
				</div>

				{/* Receipt section */}
				{renderReceiptSection()}

				{/* Beneficiary section */}
				<div css={styles.beneficiarySection}>
					{beneficiaryDTO?.logo ? (
						<InWinImage
							source={beneficiaryDTO.logo}
							alt={beneficiaryDTO.beneficiaryName}
							css={styles.beneficiaryLogoOnly}
						/>
					) : (
						<div css={styles.beneficiaryIcon}>
							<CustomIcon icon="building" style={{ color: '#6FCF97', fontSize: '16px' }} />
						</div>
					)}
					<div css={styles.beneficiaryInfo}>
						<Text size={10} css={styles.beneficiaryLabel}>
							BENEFICIARY
						</Text>
						<Text size={16} bold css={styles.beneficiaryName}>
							{beneficiaryDTO?.beneficiaryName || 'Unknown Beneficiary'}
						</Text>
					</div>
				</div>

				{/* Tournament section */}
				<div css={styles.tournamentSection}>
					<div css={styles.tournamentIcon}>
						{tournament?.logo ? (
							<InWinImage
								source={tournament.logo}
								alt={tournament.tournamentName}
								css={styles.tournamentLogo}
							/>
						) : (
							<CustomIcon icon="trophy" style={{ color: '#ffffff', fontSize: '16px' }} />
						)}
					</div>
					<div css={styles.tournamentInfo}>
						<Text size={10} css={styles.tournamentLabel}>
							TOURNAMENT
						</Text>
						<Text size={16} bold css={styles.tournamentName}>
							{tournament?.tournamentName || 'Unknown Tournament'}
						</Text>
					</div>
				</div>
			</div>
		</div>
	)
}

MobileInwinCardComponent.propTypes = {
	inwin: PropTypes.shape({
		triggerDTO: PropTypes.shape({
			eventDTO: PropTypes.shape({
				teamOne: PropTypes.shape({
					teamName: PropTypes.string,
					logo: PropTypes.string,
				}),
				teamTwo: PropTypes.shape({
					teamName: PropTypes.string,
					logo: PropTypes.string,
				}),
				tournamentDTO: PropTypes.shape({
					tournamentName: PropTypes.string,
					logo: PropTypes.string,
				}),
				sportType: PropTypes.string,
			}),
			binaryWinner: PropTypes.string,
			description: PropTypes.string,
		}),
		beneficiaryDTO: PropTypes.shape({
			beneficiaryName: PropTypes.string,
			logo: PropTypes.string,
		}),
		date: PropTypes.string,
		fullAmount: PropTypes.number,
		stateDescription: PropTypes.string,
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	}).isRequired,
}

export default MobileInwinCardComponent 